import React from 'react';
import { Database, TrendingUp, History, Search } from 'lucide-react';

const BriefingPanel = ({ displayContext, briefing, topic, onProceed, isLoading }) => {
  if (!briefing) return null;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="briefing-panel">
      {/* Topic Banner */}
      <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-xl p-4">
        <p className="text-xs text-blue-400 font-semibold uppercase tracking-wider mb-1">Strategic Question</p>
        <p className="text-white font-bold text-lg leading-snug">{topic}</p>
      </div>

      {/* Intelligence Dossier */}
      {displayContext && (
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-4">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
            <Database className="w-3.5 h-3.5" /> Intelligence Dossier
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <DossierStat
              icon={<span className="text-base">🏛️</span>}
              label="Brand"
              value={displayContext.brand_name || 'Unknown'}
              active
            />
            <DossierStat
              icon={<TrendingUp className="w-4 h-4 text-green-400" />}
              label="Metrics"
              value={displayContext.has_metrics ? 'Available' : 'Not set'}
              active={displayContext.has_metrics}
            />
            <DossierStat
              icon={<History className="w-4 h-4 text-amber-400" />}
              label="Past Debates"
              value={displayContext.past_debate_titles?.length ? `${displayContext.past_debate_titles.length} found` : 'None'}
              active={displayContext.has_past_debates}
            />
            <DossierStat
              icon={<Search className="w-4 h-4 text-purple-400" />}
              label="Hunt Intel"
              value={displayContext.has_hunt_data ? 'Available' : 'None'}
              active={displayContext.has_hunt_data}
            />
          </div>
        </div>
      )}

      {/* God Acknowledgments */}
      <div className="space-y-2">
        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider px-1">God Briefings</p>
        {briefing.map((b, i) => (
          <div
            key={b.god_id}
            className={`flex gap-3 p-3 rounded-xl border transition-all animate-fade-in ${
              b.role === 'devil_advocate'
                ? 'bg-red-500/5 border-red-500/20'
                : 'bg-gray-800/30 border-gray-700/40'
            }`}
            style={{ animationDelay: `${i * 100}ms` }}
            data-testid={`briefing-${b.god_id}`}
          >
            <span className="text-xl flex-shrink-0 mt-0.5">{b.god_emoji}</span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-gray-200">{b.god_name}</span>
                {b.role === 'devil_advocate' && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full font-semibold">
                    DEVIL'S ADVOCATE
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{b.content}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Proceed Button */}
      <button
        onClick={onProceed}
        disabled={isLoading}
        className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 flex items-center justify-center gap-2"
        data-testid="proceed-to-positions-btn"
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>Proceed to Opening Positions <span className="text-lg">→</span></>
        )}
      </button>
    </div>
  );
};

const DossierStat = ({ icon, label, value, active }) => (
  <div className={`flex items-center gap-2 p-2 rounded-lg ${active ? 'bg-gray-700/30' : 'bg-gray-800/20 opacity-50'}`}>
    {icon}
    <div>
      <p className="text-[10px] text-gray-500 uppercase">{label}</p>
      <p className="text-xs text-gray-300 font-medium">{value}</p>
    </div>
  </div>
);

export default BriefingPanel;
